import React, { useContext, useState, useEffect } from 'react';
import Debug from 'debug';
import { TrialBanner as TrialBannerMolecule } from '@bblabs/module-library/molecules';
import { CancelCircle } from '@bblabs/module-library/svgs';
import { getSettingsService } from '../services/SettingsService';
import { environment, getUrlsForEnvironment } from '../../environment';
import './TrialBanner.scss';
import { MetricService } from '../services/MetricService';
import { userPolling } from '../services/UserPollingService';
import { UserContext } from './App';

const BANNER_TIMESTAMP = 'showTrialBannerAfter';

const debug = Debug('o365:TrialBanner');

export function TrialBanner() {
  const userContext = useContext(UserContext);
  const [canShowBanner, setCanShowBanner] = useState(true);

  // determine if the user has hidden the banner by checking for
  // a timestamp in the settings. if there is a timestamp, compare it.
  // if the user still has time left, check ever 10 minutes to see if
  // the banner should appear now.
  useEffect(() => {
    function testTimestamp() {
      const settings = getSettingsService('ui');
      const ts = settings.get(BANNER_TIMESTAMP);
      const cleared = false;

      const bannerClosedByTimestamp = ts && Date.now() < ts;
      if (bannerClosedByTimestamp) {
        debug(
          'cannot see the banner yet',
          new Date().toLocaleDateString(),

          new Date(ts!).toLocaleDateString(),
        );
        setCanShowBanner(false);
      } else if (!bannerClosedByTimestamp) {
        debug('user should see the banner again, clearing setting');
        setCanShowBanner(true);
        settings.set(BANNER_TIMESTAMP, 0, true);
      }
      return [cleared, ts];
    }

    let bannerCheckInterval: ReturnType<typeof setInterval> | null = null;

    const [cleared, ts] = testTimestamp();
    if (ts && !cleared) {
      debug('start interval to check the timestamp');

      // set an interval to check if they user is now supposed to see
      // the banner again...
      bannerCheckInterval = setInterval(() => {
        const [cleared] = testTimestamp();
        if (cleared) {
          bannerCheckInterval && clearInterval(bannerCheckInterval);
        }
      }, 1000 * 60 * 10);
    }

    return () => {
      if (bannerCheckInterval) {
        clearInterval(bannerCheckInterval);
      }
    };
  }, []);

  if (
    !userContext.inTrial ||
    !userContext.buyNowUrl ||
    userContext.daysLeftInTrial === -1 ||
    !canShowBanner
  ) {
    return null;
  }

  const handleClick = () => {
    MetricService.increment('click.task_pane.trial.buy.button');
    userPolling.setPollingMode('minute');
    if (userContext.buyNowUrl) {
      window.open(userContext.buyNowUrl);
    } else {
      const urls = getUrlsForEnvironment(environment);
      window.open(`${urls.appUrl}/app/?module=users&page=subscription`);
    }

    setTimeout(() => {
      if (userContext.inTrial) {
        userPolling.setPollingMode('rapid');
      } else {
        userPolling.setPollingMode('long');
      }
    }, 1000 * 60 * 15);
  };

  return (
    <div className="banner--trial">
      <TrialBannerMolecule compact daysLeft={userContext.daysLeftInTrial} onBuyNowClick={handleClick} />
      <div className="button--container">
        <button
          className="button--close"
          type="button"
          onClick={() => {
            setCanShowBanner(false);
            const settings = getSettingsService('ui');
            settings.set('showTrialBannerAfter', Date.now() + 24 * 60 * 60 * 1000, true);
            MetricService.increment('click.task_pane.trial.close.button');
          }}
        >
          <CancelCircle title="Close banner" />
        </button>
      </div>
    </div>
  );
}
